/** @jsxImportSource @emotion/react */
import {
  StyledEngineProvider,
  ThemeOptions,
  ThemeProvider,
} from '@mui/material/styles';
import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { MainLoader, PopupAlert } from './components';
import { appConfig, BASE_ROUTE } from './constants';
import { IPlainObject, UserModel } from './models';
import { GTRoutes } from './routes/routes';
import { InterceptorService } from './services/interceptrors-service';
import sentryService from './services/sentryService';
import { SignIn, SignOut } from './store/reducers';
import { muiTheme } from './theme';
import { useCheckAuthenticationRequest } from './utils/useCheckAuthenticationRequest';

export function App(): ReactElement {
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const dispatch = useDispatch();

  const [{ data, error }, checkAuthentication] =
    useCheckAuthenticationRequest<UserModel>();

  useEffect(() => {
    InterceptorService.init(appConfig);

    checkAuthentication();
  }, []);

  useEffect(() => {
    if (!error) return;

    setIsAuthChecking(false);
    dispatch(new SignOut());
    Sentry.setUser(null);
  }, [error]);

  useEffect(() => {
    if (!data) return;

    dispatch(new SignIn(data));
    setIsAuthChecking(false);

    sentryService.setUser(data);
  }, [data]);

  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <div style={{ padding: '20px' }}>
          <h1>An error has occurred</h1>
          <p>{(error as IPlainObject)?.message}</p>
        </div>
      )}
    >
      <StyledEngineProvider injectFirst>
        <ThemeProvider<ThemeOptions> theme={muiTheme}>
          <BrowserRouter basename={BASE_ROUTE}>
            {isAuthChecking ? <MainLoader /> : <GTRoutes />}
          </BrowserRouter>
          <PopupAlert />
        </ThemeProvider>
      </StyledEngineProvider>
    </Sentry.ErrorBoundary>
  );
}
